import React, { useCallback, useMemo, useState } from 'react';

import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

export type AdminDrawerGroupProps = {
  children: NonNullable<React.ReactNode>;
  label: React.ReactNode;
  icon?: React.ReactNode;
  testId?: string;
};

export const AdminDrawerGroup: React.FC<AdminDrawerGroupProps> = ({
  label,
  icon,
  children,
  testId = 'admin-drawer-group'
}) => {
  const { hasSelected, updatedChildren } = useMemo(() => {
    let hasSelected = false;

    const updatedChildren = React.Children.map(children, x => {
      if (!React.isValidElement(x)) {
        return;
      }

      if ('to' in x.props && window.location.pathname === x.props.to) {
        hasSelected = true;
      }

      return React.cloneElement(x, {
        ...x.props,
        nested: true,
      });
    })?.filter(x => !!x);

    return {
      hasSelected,
      updatedChildren
    };
  }, [ children ]);

  const { classes } = useStyles();
  const [ open, setOpen ] = useState(hasSelected);

  const Icon = open ? ExpandLess : ExpandMore;

  const handleClick = useCallback(() => {
    setOpen(o => !o);
  }, []);

  if (!updatedChildren?.length) {
    return null;
  }

  return (
    <>
      <ListItemButton classes={classes} onClick={handleClick} data-testid={testId}>
        {icon && (
          <ListItemIcon>
            {icon}
          </ListItemIcon>
        )}
        <ListItemText primary={label} primaryTypographyProps={{ color: 'textSecondary', className: classes.text }}/>
        <Icon className={classes.icon} />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {updatedChildren}
        </List>
      </Collapse>
    </>
  );
};

const useStyles = makeStyles({ name: 'AdminDrawerGroup' })(theme => ({
  gutters: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  icon: {
    color: theme.palette.text.secondary
  },
  text: {
    fontSize: '14px'
  }
}));
